import { useApolloClient } from '@apollo/client';
import { useTranslations } from '@vocab/react';

import { useQuery, useToastMutation } from 'src/modules/apollo';
import { useAppConfig } from 'src/modules/appConfig';
import type { RetryActionKey } from 'src/modules/retry-actions/reducer';
import * as Schema from 'src/types/graphql';

import { useCandidateId } from '../data';

import translations from './.vocab';
import {
  generateUpdateProfileVisibilityOptimisticResponse,
  generateUpdateShareableProfileSettingsOptimisticResponse,
} from './apolloCache.optimistic';
import {
  updateCacheUpdateProfileVisibility,
  updateCacheUpdateShareableProfileSettings,
} from './apolloCache.updates';

export const useGetProfileVisibility = () => {
  const { language } = useAppConfig();
  const { data, loading, error } = useQuery(
    Schema.GetProfileVisibilityDocument,
    {
      variables: { languageCode: language },
    },
  );

  const profileVisibilityLevel = data?.viewer?.profileVisibility?.level || null;

  return { profileVisibilityLevel, loading, error };
};

export const useGetShareableProfileStatus = (eatAuthError: boolean = false) => {
  const { language, zone } = useAppConfig();

  const { data, loading, error } = useQuery(
    Schema.GetShareableProfileSettingsDocument,
    {
      eatAuthError,
      variables: {
        languageCode: language,
        zone,
      },
    },
  );

  const shareableProfileSettings = data?.viewer?.shareableProfileSettings;

  const isShareableProfileActive =
    loading || error ? false : shareableProfileSettings?.active || false;

  const shareableProfileSlug =
    loading || error ? null : shareableProfileSettings?.profileSlug || null;

  const shareableProfileViewCount =
    loading || error ? null : shareableProfileSettings?.views || 0;

  const shareableProfileUrl =
    loading || error ? null : shareableProfileSettings?.url || null;

  const shareableProfileDisplayUrl =
    loading || error ? null : shareableProfileSettings?.displayUrl || null;

  return {
    isShareableProfileActive,
    shareableProfileSlug,
    shareableProfileUrl,
    shareableProfileDisplayUrl,
    shareableProfileViewCount,
    loading,
    error,
  };
};

export const useUpdateProfileVisibility = () => {
  const { t } = useTranslations(translations);
  const { candidateId } = useCandidateId();

  return useToastMutation(Schema.UpdateProfileVisibilityDocument, {
    key: 'profile-visibility-v2',
    errorActionRouteParameters: { card: 'profile-visibility-v2', mode: 'EDIT' },
    update: updateCacheUpdateProfileVisibility,
    optimisticResponse: generateUpdateProfileVisibilityOptimisticResponse(
      candidateId || '',
    ),
    successToastMessage: t('Profile visibility updated'),
    failToastMessage: t('Profile visibility could not be updated'),
  });
};

export const useUpdateShareableProfileSettings = (
  copyLinkOnCompleted?: boolean,
) => {
  const apolloClient = useApolloClient();
  const { t } = useTranslations(translations);
  const { candidateId } = useCandidateId();
  const { language, zone } = useAppConfig();

  const onCompleted = (data: Schema.UpdateShareableProfileSettingsMutation) => {
    if (
      copyLinkOnCompleted &&
      'viewer' in data?.updateShareableProfileSettings
    ) {
      const profileUrl =
        data?.updateShareableProfileSettings?.viewer?.shareableProfileSettings
          ?.url;
      if (profileUrl) {
        navigator.clipboard.writeText(profileUrl);
      }
    }
  };

  return useToastMutation(Schema.UpdateShareableProfileSettingsDocument, {
    key: 'profile-visibility-v2',
    errorActionRouteParameters: { card: 'profile-visibility-v2', mode: 'EDIT' },
    update: updateCacheUpdateShareableProfileSettings,
    onCompleted,
    optimisticResponse:
      generateUpdateShareableProfileSettingsOptimisticResponse({
        _id: candidateId || '',
        apolloClient,
        languageCode: language,
        zone,
      }),
    successToastMessage: copyLinkOnCompleted
      ? t('Profile visibility updated. Link copied')
      : t('Profile visibility updated'),
    failToastMessage: t('Profile visibility could not be updated'),
  });
};

export const useCreateShareableProfileUrl = ({
  key,
}: {
  key: RetryActionKey;
}) => {
  const { t } = useTranslations(translations);

  const onCompleted = (data: Schema.CreateShareableProfileSettingsMutation) => {
    if ('viewer' in data?.createShareableProfileSettings) {
      const profileUrl =
        data?.createShareableProfileSettings?.viewer?.shareableProfileSettings
          ?.url;
      if (profileUrl) {
        navigator.clipboard.writeText(profileUrl);
      }
    }
  };

  return useToastMutation(Schema.CreateShareableProfileSettingsDocument, {
    key,
    onCompleted,
    successToastMessage: t('Profile visibility updated. Link copied'),
    failToastMessage: t('Profile visibility could not be updated'),
  });
};
